import {
	RangeSettings,
	Settings,
	MissingProductItem,
	CoverItem,
	SofaConfigurationItem,
} from 'sofa-reborn-react';

import {
	fetchVpcPending,
	fetchVpcSuccess,
	fetchVpcFailure,
	setSavedVpcConfiguration,
} from '../../checkout/actions';
import { RootState, ThunkResult } from '../..';
import { configurationService } from '../../../services';
import { ConfigurationParser } from '../../../util/ConfigurationParser';
import { ProductUtils } from '../../../util/ProductUtils';
import ThunksUtil from '../utils/ThunksUtil';

import { VpcService } from '@inter-ikea-kompis/services';
import { ProductData } from '@ikea-aoa/component-view-3d';

type IVpcData = {
	sofaData;
	products: ProductData;
	missingProducts: MissingProductItem[];
	coverItems: CoverItem[];
	rangeSettings: RangeSettings;
};

export default class VpcThunk {
	public fetch(additionalCoverAdded: boolean): ThunkResult {
		return async (dispatch, getState): Promise<void> => {
			const state = getState();
			const { savedVpcConfiguration } = state.checkout;
			if (
				savedVpcConfiguration &&
				JSON.stringify(savedVpcConfiguration.configuration['configuration']) ===
					JSON.stringify(state.sofaConfiguration.sofaConfiguration.configuration) &&
				!additionalCoverAdded
			) {
				dispatch(fetchVpcPending());
				dispatch(fetchVpcSuccess(savedVpcConfiguration.configurationId));
				return;
			} else {
				dispatch(fetchVpcPending());
				await this.fetchVpc(dispatch, state);
			}
		};
	}

	public async getVpcOrAvailableCover(
		settings: Settings,
		products: ProductData,
		missingProducts: MissingProductItem[],
		selectedConfiguration: string,
		coverItems: CoverItem[],
		rangeSettings: RangeSettings
	): Promise<CoverItem | null> {
		const vpcService = new VpcService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
			applicationName: settings.applicationName,
			dexfApplicationId: settings.dexfApplicationId,
			dexfConfigurationVersion: settings.dexfConfigurationVersion,
		});
		const cover = await vpcService
			.getConfiguration(selectedConfiguration)
			.then((sofaData) =>
				this.parseVpcData({ sofaData, products, missingProducts, coverItems, rangeSettings })
			)
			.catch(() => {
				return null;
			});

		return cover;
	}

	private parseVpcData(data: {
		sofaData;
		products: ProductData;
		missingProducts: MissingProductItem[];
		coverItems: CoverItem[];
		rangeSettings: RangeSettings;
	}): CoverItem | null {
		let cover: CoverItem | null = null;
		if (data.sofaData) {
			const { configuration } = ConfigurationParser.parseConfiguration({
				configuration: data.sofaData.configuration['configuration'],
			});

			if (configuration.children && configuration.children.length > 0) {
				const hasMissingAutoConnectProduct = this.hasMissingAutoConnectProduct(
					data,
					configuration.children
				);
				const hasMissingProduct = this.hasMissingProduct(
					data,
					hasMissingAutoConnectProduct,
					configuration.children
				);

				cover = this.getCover(
					data,
					hasMissingAutoConnectProduct,
					hasMissingProduct,
					configuration.children,
					cover
				);
			}
		}
		return cover;
	}

	private getCover(
		data: IVpcData,
		hasMissingAutoConnectProduct: boolean,
		hasMissingProduct: boolean,
		children: SofaConfigurationItem[],
		cover: CoverItem | null
	) {
		if (hasMissingAutoConnectProduct || hasMissingProduct) {
			const configurationModelIds = ProductUtils.getModelIdsForConfiguration(
				data.products,
				data.missingProducts,
				children as SofaConfigurationItem[],
				data.rangeSettings.shouldColorAllModules as boolean,
				false
			);

			const availableCovers = ProductUtils.getAllAvailableCovers(
				data.products,
				data.coverItems,
				configurationModelIds
			);
			if (availableCovers.length > 0) {
				cover = availableCovers[0];
			}
		} else if (children[0].articleId) {
			const coverName = ProductUtils.getColorIdFromProduct(data.products, children[0].articleId);
			if (coverName) {
				const coverItem = data.coverItems.find((coverItem) => {
					return coverItem.coverName == coverName;
				});
				if (coverItem) {
					cover = coverItem;
				}
			}
		}
		return cover;
	}

	private hasMissingProduct(
		data: IVpcData,
		hasMissingAutoConnectProduct: boolean,
		children: SofaConfigurationItem[]
	) {
		let hasMissingProduct = false;
		if (!hasMissingAutoConnectProduct) {
			hasMissingProduct = ThunksUtil.containsMissingProduct(
				data.products,
				data.missingProducts,
				children
			);
		}
		return hasMissingProduct;
	}

	private hasMissingAutoConnectProduct(data: IVpcData, children: SofaConfigurationItem[]) {
		let hasMissingAutoConnectProduct = false;
		hasMissingAutoConnectProduct =
			data.rangeSettings.addAutomatically || data.rangeSettings.addArmrestsAutomatically
				? ThunksUtil.hasMissingAutoConnectProduct(
						data.rangeSettings,
						data.products,
						data.missingProducts,
						children
				  )
				: false;
		return hasMissingAutoConnectProduct;
	}

	public async fetchVpc(dispatch, state: RootState) {
		const { sofaConfiguration, shoppingItems } = state.sofaConfiguration;
		const { settings, rangeSettings } = state.settings;
		const { screenshot } = state.checkout;
		const { products } = state.products;
		const { sofaModels, sofaGroups } = state.models;
		const { additionalCoverItems } = state.cover;
		try {
			const vpcService = new VpcService({
				locale: settings.locale,
				localizationPrefixApplication: settings.localizationPrefixApplication,
				localizationEndpoints: settings.localizationEndpoints,
				applicationName: settings.applicationName,
				dexfApplicationId: settings.dexfApplicationId,
				dexfConfigurationVersion: settings.dexfConfigurationVersion,
			});
			const icf = configurationService.getICF(
				sofaConfiguration,
				products,
				sofaModels,
				sofaGroups,
				rangeSettings as RangeSettings
			);
			const vpcConfiguration = await vpcService.saveConfiguration(
				sofaConfiguration,
				[...shoppingItems, ...additionalCoverItems],
				screenshot as string,
				icf
			);
			if (vpcConfiguration) {
				dispatch(fetchVpcSuccess(vpcConfiguration.configurationId));
				dispatch(setSavedVpcConfiguration(vpcConfiguration));
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			dispatch(fetchVpcFailure(error));
		}
	}
}
